import { cn } from '@strivelabs/ui-core/lib/utils';

/**
 * The colours an avatar falls back to when someone has no picture.
 *
 * Picked by name rather than by position, so the same person is the same
 * colour everywhere they appear: a board row, a task panel, a filter menu, a
 * line on a timeline.
 */
const AVATAR_COLOURS = [ '#5B7FB0', '#5E9B8A', '#9A7BA8', '#B08A57', '#C08497', '#6E8FA3' ];

const colourFor = (name: string) => {
  let hash = 0;

  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) >>> 0;
  }

  return AVATAR_COLOURS[hash % AVATAR_COLOURS.length];
};

const initial = (name: string) => name.trim().charAt(0).toUpperCase() || '?';

interface PersonAvatarProps {
  person: { display_name: string; image_url: string | null };
  /** Named on hover where the avatar stands alone, without the name beside it. */
  title?: string;
  className?: string;
}

/**
 * Someone's picture, or their initial on a colour drawn from their name.
 *
 * Deliberately not `ui-core`'s Avatar: the name-to-colour hash is what keeps
 * one person one colour across every surface, and the token set carries no
 * equivalent.
 */
export const PersonAvatar = ({ person, title, className }: PersonAvatarProps) => {
  const shared = cn(
    'inline-flex size-[22px] shrink-0 items-center justify-center rounded-full',
    'text-[9.5px] font-semibold text-white',
    className,
  );

  if (person.image_url) {
    return <img src={person.image_url} alt="" title={title} className={cn(shared, 'object-cover')} />;
  }

  return (
    <span
      className={shared}
      style={{ background: colourFor(person.display_name) }}
      title={title}
      aria-hidden={title ? undefined : true}
    >
      {initial(person.display_name)}
    </span>
  );
};
